import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { PageHeader } from '@/components/admin/PageHeader';
import { useProducerSuccess, type SuccessFilter, type SuccessLabel } from '@/hooks/useProducerSuccess';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Bug,
  CircleCheck,
  AlertTriangle,
  XCircle,
  Users,
  Calendar,
  ImageIcon,
  Ticket,
  ExternalLink,
} from 'lucide-react';

const FILTER_OPTIONS: { value: SuccessFilter; label: string }[] = [
  { value: 'all', label: 'Alla' },
  { value: 'needs_attention', label: 'Behöver hjälp' },
  { value: 'stalled', label: 'Avstannade' },
  { value: 'healthy', label: 'Aktiva' },
  { value: 'no_activities', label: 'Utan aktiviteter' },
  { value: 'no_images', label: 'Utan bilder' },
  { value: 'no_bookings', label: 'Utan bokningar' },
];

function LabelBadge({ label }: { label: SuccessLabel }) {
  switch (label) {
    case 'healthy':
      return <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">Frisk</Badge>;
    case 'needs_attention':
      return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">Behöver hjälp</Badge>;
    case 'stalled':
      return <Badge variant="destructive">Avstannad</Badge>;
  }
}

export default function AdminProducerSuccessPage() {
  const [filter, setFilter] = useState<SuccessFilter>('all');
  const { data, isLoading } = useProducerSuccess();

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const { rows } = data;
    switch (filter) {
      case 'all': return rows;
      case 'healthy': return rows.filter(r => r.label === 'healthy');
      case 'needs_attention': return rows.filter(r => r.label === 'needs_attention');
      case 'stalled': return rows.filter(r => r.label === 'stalled');
      case 'no_activities': return rows.filter(r => r.activity_count === 0);
      case 'no_images': return rows.filter(r => r.image_count === 0);
      case 'no_bookings': return rows.filter(r => r.public_activity_count > 0 && r.booking_count === 0);
      default: return rows;
    }
  }, [data, filter]);

  const s = data?.summary;

  return (
    <AdminLayout>
      <PageHeader
        title="Producer Success"
        description="Identifiera värdar som behöver aktiveringshjälp eller uppföljning"
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <SummaryCard icon={Users} value={s?.total} label="Totalt" loading={isLoading} />
        <SummaryCard icon={CircleCheck} value={s?.healthy} label="Friska" loading={isLoading} color="text-emerald-500" />
        <SummaryCard icon={AlertTriangle} value={s?.needsAttention} label="Behöver hjälp" loading={isLoading} color="text-amber-500" />
        <SummaryCard icon={XCircle} value={s?.stalled} label="Avstannade" loading={isLoading} color="text-destructive" />
        <SummaryCard icon={Calendar} value={s?.noActivities} label="Utan aktiviteter" loading={isLoading} />
        <SummaryCard icon={ImageIcon} value={s?.noImages} label="Utan bilder" loading={isLoading} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTER_OPTIONS.map(opt => (
          <Button
            key={opt.value}
            variant={filter === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(opt.value)}
          >
            {opt.label}
            {filter !== opt.value && data && (
              <span className="ml-1.5 text-muted-foreground">
                {opt.value === 'all' ? data.rows.length
                  : opt.value === 'healthy' ? data.summary.healthy
                  : opt.value === 'needs_attention' ? data.summary.needsAttention
                  : opt.value === 'stalled' ? data.summary.stalled
                  : opt.value === 'no_activities' ? data.summary.noActivities
                  : opt.value === 'no_images' ? data.summary.noImages
                  : opt.value === 'no_bookings' ? data.summary.noBookings
                  : ''}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : filteredRows.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Inga värdar matchar filtret.
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg divide-y">
          {filteredRows.map(row => (
            <div
              key={row.id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate">{row.name}</p>
                  <LabelBadge label={row.label} />
                  <Badge variant="outline" className="text-xs">{row.status}</Badge>
                </div>
                {row.issues.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {row.issues.join(' · ')}
                  </p>
                )}
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                <span className="flex items-center gap-1" title="Aktiviteter">
                  <Calendar className="h-3 w-3" /> {row.activity_count}
                </span>
                <span className="flex items-center gap-1" title="Bilder">
                  <ImageIcon className="h-3 w-3" /> {row.image_count}
                </span>
                <span className="flex items-center gap-1" title="Bokningar">
                  <Ticket className="h-3 w-3" /> {row.booking_count}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/app/admin/producers/${row.id}`}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Öppna
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/app/admin/system/host-debug?q=${row.id}`}>
                    <Bug className="h-3.5 w-3.5 mr-1" />
                    Debug
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

/* ── Summary Card ── */

function SummaryCard({
  icon: Icon,
  value,
  label,
  loading,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value?: number;
  label: string;
  loading: boolean;
  color?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-3 pb-2 px-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color || 'text-muted-foreground'}`} />
          <span className="text-xl font-bold">{loading ? '–' : (value ?? 0)}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}
